/**
 * The Book sheet (design reference v4, #sheet-book): who, the day chips (only days Open for Scheduling),
 * the day's positions as slot chips with their times from the Day plan and their state — free, taken
 * (locked, the unit shown), takeable (dashed amber: waiting to confirm, or proposed) with the warning card
 * and Take / Keep — the legend, the warnings computed before submit (the six-visit warning, surge, the
 * deep-clean day, the tenant's week, the cycle's entitlement, eligibility), the note, and the switch that
 * queues "You're scheduled — please confirm". Warnings never block: the processor is the gate.
 *
 * The same sheet reschedules (who asked for the change) and finds a day for someone on Needs a new day.
 *
 * 1.5.1 (one tab, one job): who → what (Housekeeping · Tech Support) → when → Check and book (one summary card, one
 * button). Only what can be chosen is offered: a taken or passed position, and one that fills later, is marked on the
 * day — never a button. The email switch shows only when the tenant can get email.
 *
 * 1.8.3 (D-1.8.3-3): "what" is the one ProgramPicker row — Housekeeping · Companion & Wellness · Reading & Services ·
 * Tech Support · Volunteer help (the scheduler: Housekeeping · Tech Support). This sheet is Housekeeping, as before.
 *
 * 1.5.19: a reschedule offers the visit's own day too (first, marked "this visit's day"): on it the visit's own time
 * is words in the brand colour — "This visit" — and any other time can be chosen (the request names the same day and
 * the position; the scheduler has taken that since v8j). A line taken on a move is named in the request, as on a booking.
 */
import { LANES, TWO_CREW_WARN_AT } from '@shared/day-crews';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AppointmentRow, BookingDaysResponse, BookingPreview, TenantSummary, TxSubmitResponse } from '@shared/api-types';
import type { BookProgram } from '@shared/book-programs';
import type { PositionChip } from '@shared/booking';
import { defaultChip, shortName } from '@shared/booking';
import { VISIT_LENGTH_STEPS, afterHoursWords, defaultLength, formatClock, lengthWords, offerFor, planDay, slotHours } from '@shared/dayplan';
import { serviceDateKey, serviceMinutesNow } from '@shared/dates';
import type { Role } from '@shared/roles';
import { ExtraWeekOverride } from './ExtraWeekOverride';
import { AUTH_EXHAUSTED, WEEKLY_CONFLICT, EXTRA_SUPPORT_CAP, canBookExtraSupport, canExtraVisit, overMonthlyLimit } from '@shared/extra-support';
import { isPastStart, type BookedStart, type OverlapAnswer, type ToldRow } from '@shared/day-bend';
import { isOfficeUnit } from '@shared/staff-units';
import { newFormSession, type ReasonCategory, type RequestedBy, type ServicePeriod } from '@shared/envelopes/queue1';
import { vocab } from '@shared/vocab';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { formatDayMedium, formatDayShort, formatMonthDay } from '@/lib/dates';
import { cycleMonthLabel } from '@/lib/cycle';
import { useTx } from '@/lib/tx';
import { useSimplerScreens } from '@/lib/simpler';
import { useDirtyFlag } from '@/lib/dirty';
import { pressWords, txApplied } from '@/lib/press-words';
import { postExtra } from '@/features/extra/api';
import { OutcomeGloss } from '@/components/OutcomeGloss';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { TenantSearch } from '@/components/TenantSearch';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { SummaryCard } from '@/components/Steps';
import { HouseholdCard } from '@/features/people/Household';
import { pieceList, useOnboarding } from '@/features/agreements/onboarding';
import { moveInDayReq, overlapOf } from '@/features/daybend/api';
import { WhenControl, type WhenValue } from '@/features/daybend/WhenControl';
import { OverlapChoice } from '@/features/daybend/OverlapChoice';
import { WhoNeedsToKnow } from '@/features/daybend/WhoNeedsToKnow';
import { ProgramPicker } from './ProgramPicker';

export interface BookOptions {
  tenant?: { tenantId: string; unit: string; name: string } | null;
  dayKey?: string | null;
  /** Reschedule this visit (a new day for it). */
  reschedule?: AppointmentRow | null;
  /** Find a day for someone on Needs a new day (closes their row once the booking is Applied). */
  followUp?: { id: string; tenantId: string; unit: string; name: string; appointment?: AppointmentRow | null } | null;
  /** Book → Tech Support (1.5.0): the one-question-per-screen sheet instead. */
  program?: BookProgram;
  /** 13 Oct 2026: opened from inside a program — that program's sheet alone, without the "what" row. */
  lockProgram?: boolean;
  /** 1.7.8: opened from a free gap on Today — When starts at this time (minutes after midnight, today). */
  startMinutes?: number | null;
  /** 20 Oct 2026 (D-1020-EXTRA): opened from a refused booking (or an open spot) — the extra visit / Extra support switch starts on. */
  extra?: boolean;
}

const OFFICE_REASONS = (vocab.SM_AppointmentTransactions.OutcomeReasonCategory as readonly string[]).filter(
  (r) => r !== 'Not Applicable' && r !== 'Tenant Unavailable',
) as ReasonCategory[];
const DAYS_SHOWN = 10;

interface Choice {
  chip: PositionChip;
}

export function BookSheet({ opts, onDone, onProgram, returnFocusTo, role = null }: { opts: BookOptions; onDone: () => void; /** 20 Oct 2026 (D-1020-EXTRA): who is booking — the extra visit is the administrator's; Extra support is the three booking roles'. */ role?: Role | null; /** 1.8.3: Book's "what" row — another program was pressed (the tenant chosen so far goes with it). */ onProgram?: (program: BookProgram, tenant: { tenantId: string; unit: string; name: string } | null) => void; returnFocusTo?: RefObject<HTMLElement | null> }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const announce = useAnnounce();
  const toast = useToast();
  const tx = useTx();
  const qc = useQueryClient();

  // A form session per opening: pressing Book twice sends the same keys ("already in the queue").
  // 20 Oct 2026: a refusal opens a new one — a second try with the same keys would be answered with the refused request.
  const [session, setSession] = useState(() => newFormSession());

  const followAppt = opts.followUp?.appointment ?? null;
  // a proposal, or (1.5.0) a visit still booked on a day that has since closed: Find a day moves it
  const moving: AppointmentRow | null = opts.reschedule ?? (followAppt && ['Proposed', 'Scheduled', 'On Hold'].includes(followAppt.status) ? followAppt : null);
  const mode: 'book' | 'reschedule' = moving ? 'reschedule' : 'book';
  /** 1.6.4: a facility space (the office, a shared room) holds no time on its day: its move names a day only. */
  const space = moving?.kind === 'facility';
  const fixedTenant = opts.tenant ?? (opts.followUp ? { tenantId: opts.followUp.tenantId, unit: opts.followUp.unit, name: opts.followUp.name } : null) ??
    (moving ? { tenantId: moving.tenantId, unit: moving.unit, name: moving.tenantName } : null);

  const [tenant, setTenant] = useState(fixedTenant);
  // 1.7.8: opened from a free gap on Today — the day is today and When starts at the gap's time
  const gapStart = mode === 'book' && typeof opts.startMinutes === 'number' ? opts.startMinutes : null;
  const [dayKey, setDayKey] = useState<string | null>(opts.dayKey ?? (gapStart !== null ? serviceDateKey() : null));
  /** 1.7.8 (D-1.7.8-1): When, on a booking for today — Now sends nothing new; a chip or an exact time sends `startMinutes`. */
  const [when, setWhen] = useState<WhenValue>(gapStart !== null ? { mode: 'time', minutes: gapStart } : { mode: 'now' });
  /** 1.7.8 (D-1.7.8-3): the booking would run into the next visit — the choices, until one is pressed. */
  const [overlap, setOverlap] = useState<OverlapAnswer | null>(null);
  /** After a push: the tenants whose time changed (Who needs to know), before the sheet closes. */
  const [told, setTold] = useState<ToldRow[] | null>(null);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [asking, setAsking] = useState<PositionChip | null>(null);
  const [allDays, setAllDays] = useState(false);
  const [note, setNote] = useState('');
  const [deep, setDeep] = useState(false);
  /** How long (1.5.0): null follows the setting for the kind of clean. */
  const [length, setLength] = useState<number | null>(null);
  const [email, setEmail] = useState<boolean | null>(null);
  /** 1.5.22: "Email the tenant their new time" on a move — off unless the person turns it on. */
  const [emailNew, setEmailNew] = useState(false);
  const [requestedBy, setRequestedBy] = useState<RequestedBy | null>(null);
  const [reason, setReason] = useState<ReasonCategory>('Scheduling Error');
  const [timeLabel, setTimeLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** 20 Oct 2026 (D-1020-EXTRA): the processor refused the request — its words, verbatim, until the person acts on them. */
  const [refusal, setRefusal] = useState<{ message: string; code: string | null } | null>(null);
  const refusalRef = useRef<HTMLDivElement>(null);
  /** The extra visit (the administrator's) / Extra support (the list's) switch, and the reason the administrator gives. */
  const [extraOn, setExtraOn] = useState(!!opts.extra);
  /** The processor said Authorization Exhausted for this tenant (a stale preview does not know): the extra visit stays offered. */
  const [authRefused, setAuthRefused] = useState(false);
  /** The processor said Weekly Conflict for an Extra support tenant: the administrator's extra visit (with a reason) may still do it. */
  const [weekRefused, setWeekRefused] = useState(false);
  const [extraWhy, setExtraWhy] = useState('');
  /** Eli, 20 Oct: a Move of an Extra support visit into a week that already has a visit — refused in words; the administrator's reason is the second press. */
  const [weekBlock, setWeekBlock] = useState<string | null>(null);
  const weekReasonRef = useRef<string | null>(null);
  const extraWhyRef = useRef<HTMLInputElement>(null);
  const takeYes = useRef<HTMLButtonElement>(null);
  const bookBtn = useRef<HTMLButtonElement>(null);
  /** Check and book (1.5.1): the summary card before anything is sent. */
  const [checking, setChecking] = useState(false);
  const checkHead = useRef<HTMLHeadingElement>(null);
  useDirtyFlag('book', !!choice || note !== '' || busy || !!overlap); // 1.6.5: a booking being made is never reloaded away (lib/app-update.ts)
  useEffect(() => {
    if (checking) checkHead.current?.focus();
  }, [checking]);
  useEffect(() => {
    if (refusal) refusalRef.current?.focus();
  }, [refusal]);

  const days = useQuery({ queryKey: ['booking-days'], queryFn: () => apiFetch<BookingDaysResponse>('/api/booking/days'), staleTime: 30_000 });
  // 1.5.19: the visit's own day stays (first) when moving — another time that day is a move too
  // (a booked visit, which holds a time; a proposal holds none, so its own day is left out as before)
  const ownDay = moving && moving.status === 'Scheduled' && moving.position ? moving.serviceDateKey : null;
  const everyDay = (days.data?.days ?? []).filter((d) => !moving || ownDay || d.dayKey !== moving.serviceDateKey);
  const openDays = ownDay ? [...everyDay.filter((d) => d.dayKey === ownDay), ...everyDay.filter((d) => d.dayKey !== ownDay)] : everyDay;
  useEffect(() => {
    if (!days.data) return;
    // the sheet still opens on another day (the first one); the visit's own day is one press away
    if (!dayKey || !openDays.some((d) => d.dayKey === dayKey)) setDayKey((openDays.find((d) => d.dayKey !== ownDay) ?? openDays[0])?.dayKey ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days.data]);

  const exclude = moving?.appointmentId ?? '';
  const preview = useQuery({
    queryKey: ['booking-preview', dayKey, tenant?.tenantId ?? '', exclude],
    queryFn: () =>
      apiFetch<BookingPreview>(
        `/api/booking/preview?day=${dayKey}${tenant ? `&tenantId=${encodeURIComponent(tenant.tenantId)}` : ''}${exclude ? `&exclude=${encodeURIComponent(exclude)}` : ''}`,
      ),
    enabled: !!dayKey,
    staleTime: 10_000,
  });
  const p = preview.data && preview.data.dayKey === dayKey ? preview.data : null;
  // 1.6.7 (D-1.6.7-5): is the person onboarded for Housekeeping? The scheduler decides the booking (its own
  // eligibility); this only gives the sheet its reason line, naming what is missing. An existing tenant is complete.
  const onboarding = useOnboarding(tenant?.tenantId);
  const chips = p?.positions.chips ?? [];
  /** On the visit's own day: the position it holds now (the preview leaves the visit out, so it reads free). */
  const sameDay = !!moving && dayKey === ownDay;
  const ownPosition = sameDay ? (moving?.position ?? null) : null;
  // 1.5.0: the day laid out from each visit's length; today, from the crew's running day
  const planSettings = p?.dayPlan?.settings ?? {};
  const planVisits = p?.dayPlan?.visits ?? [];
  const live = p?.dayPlan?.live ?? {};
  // 1.6.6: a two-crew day — the times are offered lane by lane, each at its own lane's clock
  const lanes = live.lanes ?? null;
  // 1.6.4: a staff unit starts at its own setting (HK_STAFF_LENGTH)
  // 1.7.8: an office (an apartment the staff-units list marks "office") starts at HK_OFFICE_LENGTH — 30 minutes
  // 15 Oct 2026: a Reschedule keeps the visit's own length; a new booking takes the setting for its kind
  const lengthMinutes = length ?? (mode === 'reschedule' && p?.moving ? p.moving.lengthMinutes : defaultLength(planSettings, deep, p?.tenant?.category === 'Staff' ? (isOfficeUnit(planSettings, p.tenant.unit) ? 'office' : 'staff') : 'tenant'));
  const plan = dayKey ? planDay(dayKey, planSettings, planVisits, chips.length, live) : [];
  const laid = (pos: number | null | undefined) => (pos ? plan.find((q) => q.position === pos) : undefined);
  /** The time a chip shows: the plan's label, or today's expected time once the crew has started. */
  const timeOf = (pos: number | null | undefined) => {
    const x = laid(pos);
    return x ? formatClock(x.expectedStart !== null && !x.booked ? x.expectedStart : x.startMinutes) : '';
  };
  const isPassed = (pos: number) => !!laid(pos)?.passed;

  // A new day (or tenant) starts on the first free position; a takeable chip needs a second press.
  useEffect(() => {
    if (!p) return;
    // 15 Oct 2026: on the visit's own day nothing else is chosen for it — it stays where it is (its time is preset
    // below); a full day never offers the surge place for a visit that already has its own
    const d = ownPosition ? null : defaultChip(p.positions);
    setChoice(d ? { chip: d } : null);
    if (ownPosition && p.moving?.start != null) setWhen({ mode: 'time', minutes: p.moving.start });
    setAsking(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p]);
  useEffect(() => {
    if (p?.tenant && email === null) setEmail(p.tenant.emailAllowed && p.pleaseConfirmDefault !== false);
  }, [p, email]);
  useEffect(() => {
    if (asking) takeYes.current?.focus();
  }, [asking]);

  const displacing = choice && (choice.chip.state === 'waiting' || choice.chip.state === 'proposed') ? choice.chip : null;
  // on the visit's own day nothing is chosen for it: the next free time may be the one it already has
  const lands = space ? null : (choice?.chip.lands ?? (sameDay ? null : (p?.positions.firstFree ?? null)));
  const base = p?.slot?.baseCapacity || 5;
  // the crew's end of day (DAY_PLAN_END) — the 6th (surge) visit keeps its typed label
  const positionOffer = dayKey && lands && lands <= base ? offerFor(dayKey, planSettings, planVisits, chips.length, { position: lands, minutes: lengthMinutes }, live) : null;
  // 1.7.8: When shows on a booking for today (a tenant, a staff unit, an office) — never a move, never a space
  // 14 Oct 2026: a move onto today takes a time too (and a move within today is a Move: the same record, a new time)
  // 15 Oct 2026 (timed appointments): every day takes a time — today and any day ahead
  const showWhen = !space && !!dayKey && dayKey >= serviceDateKey();
  const isToday = !!dayKey && dayKey === serviceDateKey();
  const chosenStart = showWhen && when.mode === 'time' ? when.minutes : null;
  // With a time chosen the visit's span is the chosen one — earlier than now included — and the end-of-day rule reads
  // it, not the position's time (the server does the same: api/src/transactions/index.ts)
  const offer =
    positionOffer && chosenStart !== null
      ? { ...positionOffer, startMinutes: chosenStart, endMinutes: chosenStart + lengthMinutes, scheduledStart: chosenStart, passed: false, fits: chosenStart + lengthMinutes <= positionOffer.endOfDay, dayOverrunsTo: null }
      : positionOffer;
  const pastEnd = !!offer && !offer.fits && !offer.passed;
  // 1.5.2: a slot past the crew's end of day is shown as "after hours"; choosing it is the choice — it is saved as
  // Awaiting the Director's OK, the tenant's email held until Approve (D-1.5.2-13)
  const afterHours = pastEnd;
  /** What a free chip says for the visit being booked (its length): free, or after hours. */
  const hoursOf = (pos: number) => (dayKey && pos <= base ? slotHours(dayKey, planSettings, planVisits, chips.length, pos, lengthMinutes, live) : 'free');
  const landsTime = offer ? formatClock(offer.startMinutes) : timeOf(lands);
  // the preview leaves the moved visit out, so it is counted once here (on its own day too: a change of time adds no one)
  const count = p ? p.positions.inkCount + (displacing?.state === 'waiting' ? 0 : 1) : 0;
  // 1.6.6: with two crews the warning comes at the 7th visit (never a block)
  const six = !sameDay && !!p && count >= (lanes ? Math.max(p.warnAt, TWO_CREW_WARN_AT) : p.warnAt);
  const surge = !!lands && lands > base;
  // Pick a spot (1.4.0): the processor books the time chosen; the 6th visit needs a label and never today.
  const pickSpot = !!p?.pickSpot;
  const sixthToday = surge && !!dayKey && dayKey <= serviceDateKey();
  const needsLabel = surge && !sixthToday && !timeLabel.trim();
  const who = p?.tenant;
  // 20 Oct 2026 (D-1020-EXTRA): past the month's limit the processor refuses; the administrator can add an extra visit
  // with a reason, and a tenant on the Extra support list (under 4 visits that month) can be booked as one by the three roles
  const over = mode === 'book' && !!who && who.category !== 'Staff' && (overMonthlyLimit(who.cycle) || authRefused || (weekRefused && !!who.extraSupport?.on));
  const director = canExtraVisit(role);
  const supportOk = !!who?.extraSupport?.on && canBookExtraSupport(role) && (who.extraSupport.monthVisits ?? 0) < EXTRA_SUPPORT_CAP;
  const extraKind: 'director' | 'support' | null = over ? (director ? 'director' : supportOk && !weekRefused ? 'support' : null) : null;
  const extraActive = extraOn && !!extraKind;
  const extraNeedsReason = extraActive && extraKind === 'director' && extraWhy.trim().length < 3;
  const extraNeedsFree = extraActive && !!displacing;
  const ineligible = !!who && who.category !== 'Staff' && !who.eligibility.eligible;
  const hkOnb = who && who.category !== 'Staff' && onboarding.data?.tenantId === who.tenantId ? (onboarding.data.onboarding?.housekeeping ?? null) : null;
  const confirmedVisit = moving?.confirmation === 'Confirmed';
  const needWho = mode === 'reschedule';
  const officeNeedsNote = requestedBy === 'office' && !note.trim();
  /** Another time on the same day needs a time chosen, and the scheduler taking the time chosen (pick a time on). */
  const sameDayStuck = sameDay && chosenStart === null && (!pickSpot || !lands || lands === ownPosition);
  /** 14 Oct 2026: a time that has passed today is the office putting a done visit on the record — it needs a reason. */
  const pastPick = isToday && chosenStart !== null && isPastStart(chosenStart, serviceMinutesNow());
  const pastNeedsNote = pastPick && !note.trim();
  // 1.8.1 (D-1.8.1-2): a home visit that ended "Declined the acknowledgement" — no Housekeeping visit until a later Ready
  const declined = !!who?.hkDeclined;
  // 3 Nov 2026 (D-1103-SIMPLE-5): Simpler screens — when Next is off, one line says what it is waiting for (Next stays off; nothing else changes)
  const simpler = useSimplerScreens();
  const stillNeeded: string[] = [];
  if (!tenant) stillNeeded.push('tenant');
  if (!dayKey) stillNeeded.push('day');
  if (needWho && !requestedBy) stillNeeded.push('who');
  if (officeNeedsNote || pastNeedsNote || extraNeedsReason) stillNeeded.push('note');
  if (sameDayStuck) stillNeeded.push('time');
  const canSubmit = !declined && !!tenant && !!dayKey && !busy && (!needWho || (!!requestedBy && !officeNeedsNote)) && !sixthToday && (!needsLabel || extraActive) && !offer?.passed && !sameDayStuck && !pastNeedsNote && !extraNeedsReason && !extraNeedsFree;

  const press = (c: PositionChip) => {
    if (c.position === ownPosition) return;
    if (!c.selectable) {
      if (c.state === 'free' || c.state === 'waiting' || c.state === 'proposed') {
        announce(p?.positions.firstFree ? t('book.fillsInOrder', { time: timeOf(p.positions.firstFree) }) : t('book.dayFull'));
      }
      return;
    }
    if (c.state === 'free') {
      setChoice({ chip: c });
      setAsking(null);
      return;
    }
    if (choice?.chip.position === c.position) return;
    setAsking(c);
  };

  const periodFor = (pos: number | null): ServicePeriod => {
    // a space keeps the part of the day it was booked for
    if (space) return moving?.servicePeriod === 'Morning' ? 'Morning' : 'Afternoon';
    const x = pos ? plan.find((q) => q.position === pos) : undefined;
    return x && x.startMinutes >= 12 * 60 ? 'Afternoon' : 'Morning';
  };

  /** What the day answered beside the booking (1.7.8): "pushed n" / "kept both", said with the outcome. */
  const bentWords = (b: BookedStart | undefined | null) =>
    !b ? '' : b.moved.length ? ` ${t('book178.pushed', { count: b.moved.length })}` : b.kept ? ` ${t('book178.keptBoth', { unit: b.kept.unit })}` : '';
  /** Closes — or, after a push that moved a tenant, shows Who needs to know first. */
  const finishBooking = (b: BookedStart | undefined | null) => {
    setOverlap(null);
    if (b?.toTell.length) setTold(b.toTell);
    else onDone();
  };

  const submit = async (onOverlap?: 'push' | 'keep' | 'swap') => {
    if (!tenant || !dayKey) return;
    setBusy(true);
    setError(null);
    setWeekBlock(null);
    const weekReason = weekReasonRef.current;
    weekReasonRef.current = null;
    const dayLabel = formatDayShort(dayKey, lang);
    const startFields = chosenStart !== null ? { startMinutes: chosenStart, ...(onOverlap ? { onOverlap } : {}) } : {};
    // 14 Oct 2026: another time on the visit's own day (today) is a Move — the same record keeps its place and takes
    // the time; nothing is cancelled and re-made, and the time cannot be lost on the way
    if (mode === 'reschedule' && sameDay && chosenStart !== null && moving) {
      try {
        // the record's reason, in the audit's own words (who asked, then the note)
        const why = [requestedBy === 'tenant' ? 'Tenant asked' : requestedBy === 'office' ? 'Office' : '', note.trim()].filter(Boolean).join(': ');
        // 17 Oct 2026: "Email the tenant their new time" travels with the move, as it does with a move to another day —
        // on: the server queues the email (the Outbox, the cancel window); off: nobody is emailed (only where it is shown)
        const switchShown = !!who?.emailAllowed && moving.kind !== 'staff';
        const res = await moveInDayReq({ appointmentId: moving.appointmentId, start: chosenStart, ...(length !== null ? { minutes: lengthMinutes } : {}), ...(why ? { why: why.slice(0, 200) } : {}), ...(onOverlap ? { onOverlap } : {}), ...(switchShown ? { emailNewTime: emailNew } : {}) });
        void qc.invalidateQueries();
        const m = [t(res.unchanged ? 'when178.move.unchanged' : 'when178.move.moved', { unit: tenant.unit, time: formatClock(res.start) }), ...(res.communicationId ? [t('when178.told.queued', { count: 1 })] : [])].join(' · ');
        toast(m);
        announce(m);
        if (res.emailNotQueued) {
          // the move is saved; the email could not be written (its wording) — said as the server wrote it
          toast(res.emailNotQueued);
          announce(res.emailNotQueued, 'assertive');
        }
        setOverlap(null);
        if (res.toTell.length) setTold(res.toTell);
        else onDone();
      } catch (e) {
        const ask = overlapOf(e);
        if (ask) setOverlap(ask);
        else {
          const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
          setError(m);
          announce(m, 'assertive');
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const body =
        mode === 'reschedule'
          ? {
              op: 'reschedule',
              session,
              appointmentId: moving!.appointmentId,
              newDayKey: dayKey,
              period: periodFor(lands),
              requestedBy: requestedBy!,
              reason: requestedBy === 'office' ? reason : undefined,
              note: note.trim() || undefined,
              shownPosition: lands,
              followUpId: opts.followUp?.id,
              ...(displacing?.line ? { displace: { appointmentId: displacing.line.appointmentId, kind: displacing.state as 'waiting' | 'proposed' } } : {}),
              ...(pickSpot && lands ? { position: lands } : {}),
              ...(surge && timeLabel.trim() ? { timeLabel: timeLabel.trim() } : {}),
              ...(pastEnd && afterHours ? { afterHours: true } : {}),
              ...(emailNew && who?.emailAllowed ? { emailNewTime: true } : {}),
              ...(weekReason ? { extraWeekReason: weekReason } : {}),
              ...startFields,
            }
          : {
              op: 'create',
              session,
              tenantId: tenant.tenantId,
              dayKey,
              period: periodFor(lands),
              note: note.trim() || undefined,
              deepClean: deep,
              lengthMinutes,
              ...(pastEnd && afterHours ? { afterHours: true } : {}),
              emailPleaseConfirm: !!email && !!who?.emailAllowed,
              displace: displacing?.line ? { appointmentId: displacing.line.appointmentId, kind: displacing.state as 'waiting' | 'proposed' } : undefined,
              shownPosition: lands,
              followUpId: opts.followUp?.id,
              ...(pickSpot && lands ? { position: lands } : {}),
              ...(surge && timeLabel.trim() ? { timeLabel: timeLabel.trim() } : {}),
              // 1.7.8: Now sends nothing new; a chosen time travels, and the answer to the overlap on the second press
              ...startFields,
            };
      if (mode === 'book' && extraActive && extraKind) {
        // 20 Oct 2026 (D-1020-EXTRA): the director's extra visit / an Extra support booking — written directly (the processor
        // has no exception path); the server enforces the role. Like every booking it asks the tenant's confirmation the usual way.
        const d = await postExtra({
          kind: extraKind,
          tenantId: tenant.tenantId,
          dayKey,
          position: lands,
          period: periodFor(lands) === 'Afternoon' ? 'Afternoon' : 'Morning',
          ...(extraKind === 'director' ? { reason: extraWhy.trim() } : {}),
          note: note.trim() || undefined,
          deepClean: deep,
          lengthMinutes,
          emailPleaseConfirm: !!email && !!who?.emailAllowed,
          ...startFields,
        });
        const m = `${t('extra1020.booked', { message: d.message })}${bentWords(d.bent)}`;
        toast(m);
        announce(m);
        if (d.emailNotQueued) {
          toast(d.emailNotQueued);
          announce(d.emailNotQueued, 'assertive');
        }
        void qc.invalidateQueries();
        finishBooking(d.bent);
        return;
      }
      if (mode === 'book' && who?.category === 'Staff') {
        // A staff unit takes a position but is not a tenant booking: written directly (D-1.1.0-10).
        const d = await apiFetch<{ message: string; bent?: BookedStart }>('/api/housekeeping/direct', {
          method: 'POST',
          body: JSON.stringify({ kind: 'staff', tenantId: tenant.tenantId, dayKey, position: lands, period: periodFor(lands), notes: note.trim() || undefined, lengthMinutes, ...startFields }),
        });
        const m = `${t('book.staffBooked', { message: d.message })}${bentWords(d.bent)}`;
        toast(m);
        announce(m);
        void qc.invalidateQueries();
        finishBooking(d.bent);
        return;
      }
      const res = await apiFetch<TxSubmitResponse>('/api/transactions/appointment', { method: 'POST', body: JSON.stringify(body) });
      const label =
        mode === 'reschedule'
          ? t('book.labelMove', { unit: tenant.unit, day: dayLabel })
          : t('book.labelBook', { unit: tenant.unit, day: dayLabel, time: landsTime || '—' });
      tx.track(res, { label, unit: tenant.unit, appointmentId: moving?.appointmentId ?? null, dayKey });
      // 20 Oct 2026 (D-1020-EXTRA): the processor refused it (the app's engine answers with the press): the sheet stays, with the
      // processor's own words — never "saved". A new form session, so a second try is a new request. (The tracker has
      // announced it and the Needs your attention item keeps it.)
      if (res.status !== 'duplicate' && !res.transaction.open && !txApplied(res.transaction)) {
        const words = res.transaction.outcomeMessage ?? t('tx.closedNoMessage', { status: res.transaction.processingStatus });
        setRefusal({ message: words, code: res.transaction.outcomeCode });
        if (res.transaction.outcomeCode === AUTH_EXHAUSTED) setAuthRefused(true);
        if (res.transaction.outcomeCode === WEEKLY_CONFLICT) setWeekRefused(true);
        setSession(newFormSession());
        setChecking(false);
        return;
      }
      const msg = `${pressWords(t, res, displacing?.line ? t('book.sentBumped', { label, unit: displacing.line.unit }) : t('tx.sent', { label }))}${bentWords(res.bent)}`;
      toast(msg);
      announce(msg);
      if (res.bent) void qc.invalidateQueries({ queryKey: ['today'] });
      finishBooking(res.bent);
    } catch (e) {
      // 1.7.8: it would run into the next visit — the sheet asks (Push · Keep both · Cancel); nothing was written
      const ask = chosenStart !== null ? overlapOf(e) : null;
      if (ask) {
        // (the step says its own sentence, takes the focus and announces it: features/daybend/OverlapChoice.tsx)
        setOverlap(ask);
        return;
      }
      setOverlap(null);
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      if (mode === 'reschedule' && e instanceof ApiRequestError && e.body?.error === 'week') setWeekBlock(m);
      else setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  const title =
    mode === 'reschedule'
      ? t('book.rescheduleTitle', { unit: tenant?.unit ?? '' })
      : opts.followUp
        ? t('book.findDayTitle', { unit: tenant?.unit ?? '' })
        : tenant
          ? `${t('sheet.bookTitle')} · ${tenant.unit}`
          : t('sheet.bookTitle');

  const bookLabel = !dayKey
    ? t('book.send')
    : extraActive
      ? t(extraKind === 'director' ? 'extra1020.bookExtra' : 'extra1020.bookSupport', { day: formatDayShort(dayKey, lang), time: landsTime || '—' })
      : mode === 'reschedule'
      ? t('book.moveTo', { day: formatDayShort(dayKey, lang), time: landsTime || '—' })
      : pastEnd && afterHours
        ? t('book.bookAfterHours', { day: formatDayShort(dayKey, lang), time: landsTime })
        : landsTime
          ? t(ineligible ? 'book.bookAnyway' : 'book.bookAt', { day: formatDayShort(dayKey, lang), time: landsTime })
          : t('book.sendAnyway');

  const chipView = (c: PositionChip) => {
    // Eli, 13 Oct 2026: on today the clock is When's alone — a spot is named by its number so two clocks never disagree
    // 15 Oct 2026: one clock — the chips say each visit's own time (chosen starts included)
    const time = timeOf(c.position);
    const pressed = choice?.chip.position === c.position;
    const unit = c.line?.unit ?? '';
    const surgeNote = c.surge ? ` · ${t('book.surgeShort')}` : '';
    // Marked, never offered (rule 4): a taken or passed position, and one that fills later, is words on the day — not a button.
    const marker = (state: string, words: string, sub: React.ReactNode) => (
      <span key={c.position} className="chip slot locked marker" data-testid={`chip-${c.position}`} data-state={state} data-offered="false">
        <span className="t tnum" aria-hidden="true">
          {time}
        </span>
        <span className="sub" aria-hidden="true">
          {sub}
        </span>
        <span className="sr-only">{words}</span>
      </span>
    );
    if (c.position === ownPosition) {
      // this visit's own time: words in the brand colour, the unit shown — never a button
      return (
        <span key={c.position} className="chip slot locked marker own" data-testid={`chip-${c.position}`} data-state="own" data-offered="false">
          <span className="t tnum" aria-hidden="true">
            {time}
          </span>
          <span className="sub" aria-hidden="true">
            {moving?.unit} · {t('book.thisVisit')}
          </span>
          <span className="sr-only">{t('book.chipOwn', { time, unit: moving?.unit ?? '' })}</span>
        </span>
      );
    }
    if (c.state === 'taken') {
      const what = c.line?.confirmation === 'Confirmed' ? t('book.confirmedLow') : t('book.bookedLow');
      return marker(
        'taken',
        t('book.chipTaken', { time, unit, what }) + surgeNote,
        <>
          <Icon name="lock" aria-hidden="true" />
          {unit} · {what}
        </>,
      );
    }
    if ((c.state === 'waiting' || c.state === 'proposed') && !c.selectable) {
      const state = c.state === 'waiting' ? t('book.waitingLow') : t('book.proposedLow');
      return marker(c.state, `${time}, ${unit} ${state}. ${t('book.cannotTakeYet')}${surgeNote}`, `${unit} · ${state}`);
    }
    if (c.state === 'waiting' || c.state === 'proposed') {
      const state = c.state === 'waiting' ? t('book.waitingLow') : t('book.proposedLow');
      const replacing = pressed && tenant ? t('book.replacing', { unit: tenant.unit, other: unit }) : `${unit} · ${state}`;
      return (
        <button
          key={c.position}
          type="button"
          className="chip slot"
          data-soft={c.state}
          aria-pressed={pressed}
          aria-label={`${time}, ${unit} ${state}. ${t('book.canTake')}${surgeNote}`}
          onClick={() => press(c)}
          data-testid={`chip-${c.position}`}
          data-state={c.state}
        >
          <span className="t tnum">{time}</span>
          <span className="sub">{replacing}</span>
        </button>
      );
    }
    if (isPassed(c.position)) return marker('passed', t('book.chipPassed', { time }), t('book.passedLow'));
    if (!c.selectable) return marker('free', `${time}, ${t('book.freeLow')}${surgeNote}. ${t('book.fillsInOrder', { time: timeOf(p?.positions.firstFree) })}`, t('book.laterLow'));
    const late = !c.surge && hoursOf(c.position) === 'afterHours';
    const word = late ? t('book.afterHoursLow') : c.surge ? t('book.surgeFree') : t('book.freeLow');
    return (
      <button
        key={c.position}
        type="button"
        className="chip slot"
        data-soft={late ? 'hold' : undefined}
        aria-pressed={pressed}
        aria-label={`${time}, ${late ? t('book.afterHoursLow') : t('book.freeLow')}${surgeNote}`}
        onClick={() => press(c)}
        data-testid={`chip-${c.position}`}
        data-state={late ? 'afterHours' : 'free'}
      >
        <span className="t tnum">{time}</span>
        <span className="sub">
          {c.surge ? <Icon name="bolt" aria-hidden="true" /> : late ? <Icon name="alert" aria-hidden="true" /> : null}
          {word}
        </span>
      </button>
    );
  };

  const shownDays = allDays ? openDays : openDays.slice(0, DAYS_SHOWN);

  return (
    <SheetContent
      title={title}
      returnFocusTo={returnFocusTo}
      onOpenAutoFocus={(e) => {
        // Start where the work starts: the tenant box, or (tenant known) the chosen day.
        const root = e.currentTarget as HTMLElement | null;
        const target = root?.querySelector<HTMLElement>('[role="combobox"]') ?? root?.querySelector<HTMLElement>('[data-testid="day-chips"] [aria-pressed="true"]');
        if (target) {
          e.preventDefault();
          target.focus();
        }
      }}
      data-testid="book-sheet"
      footer={
        overlap || told ? null : checking ? (
          <>
            <Button variant="secondary" type="button" onClick={() => setChecking(false)} data-testid="book-back">
              {t('book.back')}
            </Button>
            <Button type="button" onClick={() => submit()} disabled={!canSubmit} busyLabel={t(mode === 'reschedule' ? 'busy1023.ing.move' : 'busy1023.ing.book')} doneLabel={t('busy1024.sent')} data-testid="book-submit">
              {busy ? t('book.sending') : bookLabel}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" type="button" onClick={onDone}>
              {t('book.cancel')}
            </Button>
            <Button ref={bookBtn} type="button" onClick={() => setChecking(true)} disabled={!canSubmit} aria-describedby={simpler && !canSubmit && stillNeeded.length ? 'book-still-needed' : undefined} data-testid="book-next">
              {t('book.check')}
            </Button>
          </>
        )
      }
    >
      {refusal ? (
        <div className="warn take" role="alert" tabIndex={-1} ref={refusalRef} data-testid="book-refused">
          <Icon name="alert" />
          <div>
            <b>{t(mode === 'book' ? 'extra1020.refusedTitle' : 'extra1020.refusedMoveTitle')}</b>
            <br />
            <span data-testid="book-refused-message">{refusal.message}</span>
            <OutcomeGloss code={refusal.code} />
            <br />
            <span className="help">{t('extra1020.refusedKept')}</span>
            {mode === 'book' && (refusal.code === AUTH_EXHAUSTED || (refusal.code === WEEKLY_CONFLICT && !!who?.extraSupport?.on)) ? (
              extraKind ? (
                <div className="row">
                  <Button
                    size="sm"
                    type="button"
                    onClick={() => {
                      setRefusal(null);
                      setExtraOn(true);
                      setChecking(false);
                      window.setTimeout(() => (extraWhyRef.current ?? bookBtn.current)?.focus(), 50);
                    }}
                    data-testid="refused-use-extra"
                  >
                    {t(extraKind === 'director' ? 'extra1020.useExtra' : 'extra1020.useSupport')}
                  </Button>
                </div>
              ) : (
                <span className="help" data-testid="refused-only-admin">
                  {' '}
                  {t(refusal.code === WEEKLY_CONFLICT ? 'extra1020.onlyAdminWeek' : 'extra1020.onlyAdmin')}
                </span>
              )
            ) : null}
          </div>
        </div>
      ) : null}
      {told ? (
        <div className="book-step" data-testid="book-told-step">
          <WhoNeedsToKnow rows={told} onDone={onDone} />
        </div>
      ) : overlap && tenant ? (
        <div className="book-step" data-testid="book-overlap-step">
          <p className="help" data-testid="book-overlap-what">
            {t('book178.overlapWhat', { unit: tenant.unit, time: chosenStart !== null ? formatClock(chosenStart) : '', len: lengthWords(lengthMinutes) })}
          </p>
          <OverlapChoice
            answer={overlap}
            unit={tenant.unit}
            busy={busy}
            onPush={() => void submit('push')}
            onSwap={() => void submit('swap')}
            onKeep={() => void submit('keep')}
            onCancel={() => {
              // back to the form: the time, the length or the day can be changed
              setOverlap(null);
              setChecking(false);
              setError(null);
              window.setTimeout(() => bookBtn.current?.focus(), 0);
            }}
          />
        </div>
      ) : checking && tenant && dayKey ? (
        <>
          <h3 ref={checkHead} tabIndex={-1} className="step-q" data-testid="book-check-title">
            {t('book.checkTitle')}
          </h3>
          <SummaryCard
            testId="book-confirm"
            rows={[
              { label: t('book.c.who'), value: `${tenant.unit} · ${tenant.name}` },
              { label: t('book.c.what'), value: [t('programs.housekeeping'), deep ? t('book.c.deep') : '', lengthWords(lengthMinutes)].filter(Boolean).join(' · ') },
              { label: t('book.c.when'), value: `${formatDayMedium(dayKey, lang)}${landsTime ? ` · ${landsTime}` : ''}${surge && timeLabel.trim() ? ` · ${timeLabel.trim()}` : ''}${pastEnd && afterHours ? ` · ${t('approval.badge')}` : ''}` },
              ...(moving ? [{ label: t('book.c.from'), value: `${formatDayMedium(moving.serviceDateKey, lang)}${sameDay && ownPosition && timeOf(ownPosition) ? ` · ${timeOf(ownPosition)}` : ''}` }] : []),
              ...(needWho && requestedBy ? [{ label: t('book.c.asked'), value: requestedBy === 'tenant' ? t('book.c.askedTenant') : t('book.c.askedOffice') }] : []),
              ...(displacing?.line ? [{ label: t('book.c.moves'), value: t('book.c.movesWho', { unit: displacing.line.unit }) }] : []),
              ...(note.trim() ? [{ label: t('book.c.note'), value: note.trim() }] : []),
              ...(extraActive ? [{ label: t('extra1020.c.extra'), value: extraKind === 'director' ? t('extra1020.c.director', { why: extraWhy.trim() }) : t('extra1020.c.support') }] : []),
              ...(mode === 'book' ? [{ label: t('book.c.email'), value: email && who?.emailAllowed ? t('book.c.emailYes') : t('book.c.emailNo') }] : []),
            ]}
          />
        </>
      ) : (
        <>
      {moving ? (
        <p className="help" data-testid="moving-from">
          {t('book.nowOn', { day: formatDayMedium(moving.serviceDateKey, lang), status: t(`book.statusWord.${moving.status}`, { defaultValue: moving.status }) })}
        </p>
      ) : null}

      <div className="field">
        {tenant ? (
          <div className="who-picked" data-testid="book-tenant">
            <span className="eyebrow">{t('book.who')}</span>
            <b className="tnum">{tenant.unit}</b> · {tenant.name}
            {!fixedTenant ? (
              <Button size="sm" variant="ghost" type="button" onClick={() => { setTenant(null); setRefusal(null); setExtraOn(false); setAuthRefused(false); setWeekRefused(false); }}>
                {t('book.change')}
              </Button>
            ) : null}
            <HouseholdCard tenantId={tenant.tenantId} testId="book-household" />
          </div>
        ) : (
          <TenantSearch
            label={t('book.who')}
            placeholder={t('book.whoPlaceholder')}
            testId="book-tenant-search"
            program="housekeeping"
            onOpen={(s: TenantSummary) => {
              setTenant({ tenantId: s.tenantId, unit: s.unit, name: s.name });
              setEmail(null);
              setRefusal(null);
              setAuthRefused(false);
              setWeekRefused(false);
              setExtraOn(false);
            }}
          />
        )}
      </div>

      {mode === 'book' && !opts.followUp && onProgram ? <ProgramPicker value="housekeeping" onChange={(p) => onProgram(p, tenant)} /> : null}

      <div className="field">
        <span className="eyebrow" id="book-day-label">
          {t('book.day')}
        </span>
        {days.isLoading ? (
          <p className="help">{t('app.loading')}</p>
        ) : openDays.length === 0 ? (
          <p className="help" role="status">
            {t('book.noDays')}
          </p>
        ) : (
          <div className="chips" role="group" aria-labelledby="book-day-label" data-testid="day-chips">
            {shownDays.map((d) => (
              <button
                key={d.dayKey}
                type="button"
                className={d.dayKey === ownDay ? 'chip slot own-day' : 'chip slot'}
                data-own={d.dayKey === ownDay ? 'true' : undefined}
                aria-pressed={d.dayKey === dayKey}
                aria-label={`${formatDayMedium(d.dayKey, lang)}, ${t('book.dayCount', { n: d.booked, cap: d.base })}${d.dayKey === ownDay ? `, ${t('book.ownDay')}` : ''}`}
                onClick={() => {
                  setDayKey(d.dayKey);
                  setError(null);
                  setRefusal(null);
                }}
                data-testid={`day-${d.dayKey}`}
              >
                <span className="t">{formatDayShort(d.dayKey, lang)}</span>
                <span className="sub tnum">{t('book.dayCount', { n: d.booked, cap: d.base })}</span>
                {d.dayKey === ownDay ? <span className="sub own-word">{t('book.ownDay')}</span> : null}
              </button>
            ))}
            {openDays.length > DAYS_SHOWN ? (
              <Button size="sm" variant="ghost" type="button" onClick={() => setAllDays((v) => !v)} aria-expanded={allDays}>
                {allDays ? t('book.fewerDays') : t('book.moreDays', { count: openDays.length - DAYS_SHOWN })}
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {space ? (
        <p className="help" data-testid="space-no-time">
          {t('book.spaceNoTime')}
        </p>
      ) : null}
      <div className="field" style={space ? { display: 'none' } : undefined}>
        <span className="eyebrow" id="book-slot-label">
          {showWhen ? t('book.slotToday') : t('book.slot')}
        </span>
        {showWhen ? (
          <p className="help" data-testid="slot-today-help">
            {t('book.slotTodayHelp')}
          </p>
        ) : null}
        {!dayKey ? null : preview.isLoading || !p ? (
          <p className="help">{t('app.loading')}</p>
        ) : (
          <>
            {lanes ? (
              <div className="lane-chips" data-testid="slot-chips">
                <p className="help" data-testid="slot-lanes-help">
                  {t('crews166.bookLaneHelp')}
                </p>
                {LANES.map((l) => {
                  const mine = chips.filter((c) => lanes[c.position]?.crew === l).sort((a, b) => lanes[a.position]!.n - lanes[b.position]!.n);
                  return mine.length ? (
                    <div key={l} className="lane" role="group" aria-label={t('crews166.bookLane', { crew: l })} data-testid={`slot-lane-${l}`}>
                      <span className="status crew-tag has-icon">
                        <Icon name="people" />
                        {t('crews166.bookLane', { crew: l })}
                      </span>
                      <div className="chips">{mine.map(chipView)}</div>
                    </div>
                  ) : null;
                })}
              </div>
            ) : (
              <div className="chips" role="group" aria-labelledby="book-slot-label" data-testid="slot-chips">
                {chips.map(chipView)}
              </div>
            )}
            <div className="legend" style={{ marginTop: 10 }} data-testid="slot-legend">
              <span>
                <i style={{ background: 'var(--st-conf-dot)' }} />
                {t('book.lgFree')}
              </span>
              <span>
                <i style={{ background: 'var(--st-hold-dot)' }} />
                {t('book.lgSoft')}
              </span>
              <span>
                <i style={{ background: 'var(--st-done-dot)' }} />
                {t('book.lgLocked')}
              </span>
            </div>
            <p className="help" data-testid="slot-rule">
              {pickSpot ? t('book.pickAny') : t('book.slotFrom')}
            </p>
            {sameDay && !pickSpot ? (
              <p className="help" role="status" data-testid="same-day-off">
                {t('book.sameDayOff')}
              </p>
            ) : sameDay && !lands ? (
              <p className="help" role="status" data-testid="same-day-choose">
                {t('book.sameDayChoose')}
              </p>
            ) : null}
            {offer ? (
              <p className="help" data-testid="slot-span">
                {t(chosenStart !== null ? 'book178.span' : 'book.span', { from: formatClock(offer.startMinutes), to: formatClock(offer.endMinutes), len: lengthWords(lengthMinutes) })}
                {offer.startMinutes !== offer.scheduledStart ? ` · ${t('book.plannedWas', { time: formatClock(offer.scheduledStart) })}` : ''}
              </p>
            ) : null}
            {p.positions.extraProposals.length ? <p className="help">{t('book.extraProposals', { count: p.positions.extraProposals.length })}</p> : null}
            {!sameDay && !p.positions.firstFree && !chips.some((c) => c.selectable) ? (
              <p className="help" role="status" data-testid="day-full">
                {t('book.dayFull')}
              </p>
            ) : null}
            {asking ? (
              <div className="warn take" role="group" aria-labelledby="take-title" data-testid="take-warning">
                <Icon name="alert" />
                <div>
                  <b id="take-title">
                    {t(asking.state === 'waiting' ? 'book.takeTitleWaiting' : 'book.takeTitleProposed', {
                      time: timeOf(asking.position),
                      who: shortName(asking.line?.tenantName ?? ''),
                      unit: asking.line?.unit ?? '',
                    })}
                  </b>
                  <br />
                  <span>{t('book.takeBody', { who: shortName(asking.line?.tenantName ?? ''), time: timeOf(asking.lands) })}</span>
                  <div className="row">
                    <Button
                      ref={takeYes}
                      size="sm"
                      type="button"
                      onClick={() => {
                        setChoice({ chip: asking });
                        setAsking(null);
                        announce(t('book.taken', { time: timeOf(asking.lands) }));
                        window.setTimeout(() => bookBtn.current?.focus(), 0);
                      }}
                      data-testid="take-yes"
                    >
                      {t('book.take', { time: timeOf(asking.position) })}
                    </Button>
                    <Button size="sm" variant="secondary" type="button" onClick={() => setAsking(null)} data-testid="take-no">
                      {choice ? t('book.keep', { time: timeOf(choice.chip.lands) }) : t('book.keepNone')}
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      {showWhen && dayKey && p ? (
        <div className="field book-when" data-testid="book-when-block">
          <WhenControl
            id="book-when"
            day={dayKey}
            value={when}
            onChange={(v) => {
              setWhen(v);
              setError(null);
            }}
            allowFuture
            {...(isToday ? {} : { nowLabel: t('book.whenNextFree') })}
            suggest={gapStart !== null ? { minutes: gapStart, label: t('book178.freeFrom', { time: formatClock(gapStart) }) } : null}
          />
          <p className="help" data-testid="book-when-help">
            {chosenStart === null
              ? t('book178.nowHelp')
              : lands
                ? t('book178.timeHelp', { time: formatClock(chosenStart), place: lands })
                : t('book178.timeHelpNoPlace', { time: formatClock(chosenStart) })}
          </p>
          {chosenStart !== null && !positionOffer ? (
            <p className="help" data-testid="book-when-span">
              {t('book178.span', { from: formatClock(chosenStart), to: formatClock(chosenStart + lengthMinutes), len: lengthWords(lengthMinutes) })}
            </p>
          ) : null}
        </div>
      ) : null}

      {p ? (
        <div className="warnings" aria-live="polite" data-testid="book-warnings">
          {!p.open ? (
            <div className="warn take" data-testid="warn-closed">
              <Icon name="alert" />
              <div>{t('book.notOpen')}</div>
            </div>
          ) : null}
          {declined && who?.hkDeclined ? (
            <div className="warn take" role="alert" data-testid="book-declined">
              <Icon name="lock" />
              <div>
                <b>{t('hv181.declinedLine', { date: formatMonthDay(who.hkDeclined.date.replace(/-/g, ''), lang) })}</b>
                <br />
                <span>{t('hv181.declinedClears')}</span>
              </div>
            </div>
          ) : null}
          {ineligible && who && !declined ? (
            <div className="warn take" data-testid="warn-ineligible">
              <Icon name="alert" />
              <div>
                <b>{t('book.ineligibleTitle', { unit: who.unit })}</b>
                <ul>
                  {who.eligibility.missing.map((m) => (
                    <li key={m.key}>{t(`eligibility.${m.key}`, { detail: m.detail ?? '' })}</li>
                  ))}
                </ul>
                <span>{t('book.ineligibleBody')}</span>
                {!who.enrollmentId ? (
                  <p data-testid="book-not-enrolled">
                    {t('book.notEnrolledTitle', { name: who.name })}{' '}
                    <Link to={`/people/${encodeURIComponent(who.tenantId)}`} onClick={onDone}>
                      {t('book.notEnrolledLink')}
                    </Link>
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          {hkOnb && !hkOnb.complete && who ? (
            <div className="warn take" data-testid="warn-onboarding">
              <Icon name="alert" />
              <div>
                <b data-testid="book-onboarding-reason">
                  {t('onb167.reason', { name: who.name, program: t('onb167.program.housekeeping'), done: hkOnb.done, total: hkOnb.total, list: pieceList(t, lang, hkOnb.missing) })}
                </b>{' '}
                <span>{t('onb167.hkNote')}</span>
              </div>
            </div>
          ) : null}
          {six ? (
            <div className="warn" data-testid="warn-six">
              <Icon name="alert" />
              <div>
                <b>{t('book.sixTitle', { n: count })}</b>
                <br />
                <span>{lanes ? t('crews166.sevenBody', { n: count }) : t('book.sixBody', { n: count })}</span>
                {surge ? (
                  <>
                    <br />
                    <span data-testid="warn-surge">{t('book.surgeBody', { n: lands })}</span>
                  </>
                ) : null}
              </div>
            </div>
          ) : surge ? (
            <div className="warn" data-testid="warn-surge">
              <Icon name="alert" />
              <div>{t('book.surgeBody', { n: lands })}</div>
            </div>
          ) : null}
          {p.positions.deepCount > 0 ? (
            <div className="warn" data-testid="warn-deep">
              <Icon name="info" />
              <div>
                <b>{t('book.deepTitle', { count: p.positions.deepCount })}</b>
                <br />
                <span>{t('book.stillFit', { count: p.stillFit })}</span>
              </div>
            </div>
          ) : null}
          {who?.weekConflict ? (
            <div className="warn" data-testid="warn-week">
              <Icon name="alert" />
              <div>{t('book.weekConflict', { unit: who.unit, day: formatDayMedium(who.weekConflict.serviceDateKey, lang) })}</div>
            </div>
          ) : null}
          {who?.cycle && who.cycle.remaining !== null && who.cycle.remaining <= 1 ? (
            <div className="warn" data-testid="warn-cycle">
              <Icon name="alert" />
              <div>
                {who.cycle.remaining === 0
                  ? t('book.cycleUsed', { unit: who.unit, n: who.cycle.used, of: who.cycle.authorized, cycle: cycleMonthLabel(who.cycle.cycle, lang) })
                  : t('book.cycleLast', { unit: who.unit, cycle: cycleMonthLabel(who.cycle.cycle, lang) })}
              </div>
            </div>
          ) : null}
          {over && !refusal ? (
            <div className="warn" data-testid="extra-visit-block">
              <Icon name="info" />
              <div>
                {extraKind ? (
                  <>
                    <label className="switch">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={extraOn}
                        onChange={(e) => {
                          setExtraOn(e.target.checked);
                          if (e.target.checked && extraKind === 'director') window.setTimeout(() => extraWhyRef.current?.focus(), 50);
                        }}
                        aria-describedby="extra-visit-help"
                        data-testid="extra-visit-switch"
                      />
                      <span>{t(extraKind === 'director' ? 'extra1020.switchDirector' : 'extra1020.switchSupport')}</span>
                    </label>
                    <span className="help" id="extra-visit-help">
                      {extraKind === 'director' ? t(weekRefused && !overMonthlyLimit(who?.cycle) && !authRefused ? 'extra1020.helpDirectorWeek' : 'extra1020.helpDirector') : t('extra1020.helpSupport', { n: who?.extraSupport?.monthVisits ?? 0, cap: EXTRA_SUPPORT_CAP })}
                    </span>
                    {extraOn && extraKind === 'director' ? (
                      <div className="field">
                        <label htmlFor="extra-why">{t('extra1020.why')}</label>
                        <input
                          id="extra-why"
                          ref={extraWhyRef}
                          className="input"
                          value={extraWhy}
                          maxLength={200}
                          onChange={(e) => setExtraWhy(e.target.value)}
                          aria-required="true"
                          aria-describedby="extra-why-h"
                          placeholder={t('extra1020.whyPlaceholder')}
                          data-testid="extra-why"
                        />
                        <span className="help" id="extra-why-h">
                          {extraNeedsReason ? t('extra1020.whyNeeded') : t('extra1020.whyHelp')}
                        </span>
                      </div>
                    ) : null}
                    {extraNeedsFree ? (
                      <p className="help" role="status" data-testid="extra-needs-free">
                        {t('extra1020.needsFree')}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <span data-testid="extra-not-allowed">{who?.extraSupport?.on && canBookExtraSupport(role) ? t('extra1020.atCap', { n: who.extraSupport.monthVisits, cap: EXTRA_SUPPORT_CAP }) : t('extra1020.onlyAdmin')}</span>
                )}
              </div>
            </div>
          ) : null}
          {offer?.passed ? (
            <div className="warn take" role="alert" data-testid="warn-passed">
              <Icon name="alert" />
              <div>
                {t('book.passedBody', { time: timeOf(lands) })}
                {showWhen ? <> {t('book178.passedOrWhen')}</> : null}
              </div>
            </div>
          ) : null}
          {pastEnd && offer ? (
            <div className="warn take" data-testid="warn-after-hours">
              <Icon name="alert" />
              <div>
                <b>{afterHoursWords(offer, lang === 'es' ? 'es' : 'en')}</b>
                <br />
                <span className="help">{t('book.afterHoursHelp')}</span>
              </div>
            </div>
          ) : null}
          {offer?.fits && offer.dayOverrunsTo ? (
            <div className="warn" data-testid="warn-day-overruns">
              <Icon name="alert" />
              <div>{t('book.dayOverruns', { time: formatClock(offer.dayOverrunsTo), end: formatClock(offer.endOfDay) })}</div>
            </div>
          ) : null}
          {sixthToday ? (
            <div className="warn take" role="alert" data-testid="warn-sixth-today">
              <Icon name="alert" />
              <div>{t('book.sixthToday')}</div>
            </div>
          ) : null}
          {six || surge || ineligible || who?.weekConflict || (who?.cycle?.remaining ?? 2) <= 0 || !p.open ? (
            <p className="help">{t('book.warnNeverBlock')}</p>
          ) : null}
        </div>
      ) : null}

      {needWho ? (
        <fieldset className="field" data-testid="who-asked">
          <legend>{t('book.whoAsked')}</legend>
          {confirmedVisit ? <p className="help">{t('book.whoHelp')}</p> : null}
          {/* 1.6.4: a staff unit or an office is moved without a tenant's checks (Queue 1 v8o), as it is reopened */}
          {moving && moving.kind !== 'tenant' ? (
            <p className="help" data-testid="move-no-checks">
              {t(moving.kind === 'staff' ? 'actions.reopen.staffUnit' : 'actions.reopen.office')}
            </p>
          ) : null}
          <label className="radio">
            <input type="radio" name="who-asked" checked={requestedBy === 'tenant'} onChange={() => setRequestedBy('tenant')} />
            <span>{t('book.whoTenant')}</span>
          </label>
          <label className="radio">
            <input type="radio" name="who-asked" checked={requestedBy === 'office'} onChange={() => setRequestedBy('office')} />
            <span>{t('book.whoOffice')}</span>
          </label>
          {requestedBy === 'office' ? (
            <div className="field">
              <label htmlFor="book-reason">{t('book.reason')}</label>
              <select id="book-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value as ReasonCategory)}>
                {OFFICE_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`reason.${r}`, { defaultValue: r })}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {surge && !sixthToday ? (
        <div className="field" data-testid="sixth-label">
          <label htmlFor="book-time-label">{t('book.timeLabel')}</label>
          <input
            id="book-time-label"
            className="input"
            value={timeLabel}
            maxLength={60}
            onChange={(e) => setTimeLabel(e.target.value)}
            aria-required="true"
            aria-describedby="book-time-label-h"
            placeholder={t('book.timeLabelPlaceholder')}
            data-testid="time-label"
          />
          <span className="help" id="book-time-label-h">
            {t('book.timeLabelHelp')}
          </span>
        </div>
      ) : null}

      {mode === 'book' ? (
        <label className="switch">
          <input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} data-testid="deep-clean" />
          <span>{t('book.deepAsked')}</span>
        </label>
      ) : null}

      {mode === 'book' && tenant ? (
        <div className="field">
          <label htmlFor="book-length">{t('book.howLong')}</label>
          <select
            id="book-length"
            className="input"
            value={lengthMinutes}
            onChange={(e) => {
              setLength(Number(e.target.value));
            }}
            aria-describedby="book-length-h"
            data-testid="book-length"
          >
            {VISIT_LENGTH_STEPS.map((m) => (
              <option key={m} value={m}>
                {lengthWords(m)}
              </option>
            ))}
          </select>
          <span className="help" id="book-length-h">
            {length === null ? t('book.howLongPreset', { len: lengthWords(lengthMinutes) }) : t('book.howLongChosen')}
          </span>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="book-note">{requestedBy === 'office' ? t('book.noteWhy') : t('book.noteOpt')}</label>
        <textarea
          id="book-note"
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-required={requestedBy === 'office' || undefined}
          placeholder={t('book.notePlaceholder')}
        />
        {officeNeedsNote ? <p className="help">{t('book.noteNeeded')}</p> : null}
        {pastNeedsNote ? (
          <p className="help" role="status" data-testid="book-past-why">
            {t('book.pastWhy')}
          </p>
        ) : null}
      </div>

      {mode === 'book' && tenant ? (
        <div className="field">
          {who?.emailAllowed ? (
            <label className="switch">
              <input type="checkbox" checked={!!email} onChange={(e) => setEmail(e.target.checked)} data-testid="email-switch" />
              <span>{t('book.queueEmail')}</span>
            </label>
          ) : null}
          {who && !who.emailAllowed ? <p className="help">{t('book.noEmail', { unit: who.unit })}</p> : null}
          {pastEnd && afterHours && email ? <p className="help" data-testid="email-waits">{t('book.emailWaits')}</p> : null}
        </div>
      ) : null}

      {mode === 'reschedule' && tenant && who && moving?.kind !== 'staff' ? (
        <div className="field">
          {who.emailAllowed ? (
            <label className="switch">
              <input type="checkbox" checked={emailNew} onChange={(e) => setEmailNew(e.target.checked)} data-testid="email-new-time" />
              <span>{t('moveEmail.switch')}</span>
            </label>
          ) : (
            <p className="help" data-testid="email-new-time-none">
              {t('moveEmail.none')}
            </p>
          )}
          {who.emailAllowed ? <p className="help">{t(emailNew ? 'moveEmail.on' : 'moveEmail.off')}</p> : null}
        </div>
      ) : null}
        </>
      )}

      {weekBlock ? (
        <ExtraWeekOverride
          message={weekBlock}
          isAdmin={canExtraVisit(role)}
          busy={busy}
          onConfirm={(reason) => {
            weekReasonRef.current = reason;
            void submit();
          }}
        />
      ) : null}
      {simpler && !canSubmit && !busy && !checking && !overlap && !told && stillNeeded.length ? (
        <p className="help" id="book-still-needed" data-testid="book-still-needed">
          {t('simp1103.book.stillNeeded', { list: stillNeeded.map((k) => t(`simp1103.book.need.${k}`)).join(', ') })}
        </p>
      ) : null}
      {error ? (
        <div className="warn take" role="alert" data-testid="book-error">
          <Icon name="alert" />
          <div>{error}</div>
        </div>
      ) : null}
    </SheetContent>
  );
}
